import { useTranslation } from "react-i18next";
import useStyles from "./styles";
import { Sidebar } from "../../containers/Sidebar/Sidebar";
import { CustomTypography } from "../../components/CustomTypography/CustomTypography";
import { FleetInfos } from "./FleetInfos/FleetInfos";
import { CustomInfos } from "./CustomInfos/CustomInfos";
import { RevenueInfos } from "./RevenueInfos/RevenueInfos";
import { colors } from "../../styles/colors";
import { useDashboardStats } from "../../api/dashboard";
import { usePlatformRevenue } from "../../api/revenue";
import { CustomLoader } from "../../components/CustomLoader/CustomLoader";

type Props = {};

export const Dashboard: React.FC<Props> = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { stats, isPending } = useDashboardStats();
  // Separate query rather than an extra field on /dashboard/stats: revenue
  // aggregates scan the invoice table and are gated on a different
  // permission, so folding them in would make the whole dashboard fail (or
  // wait) on the slowest, most restricted part of it.
  const { revenue, isPending: isRevenuePending } = usePlatformRevenue();

  // Format percentage to avoid misleading "0%" when users exist
  const formatPercent = (percent: number, count: number): string => {
    if (count > 0 && percent < 1) return "<1";
    if (count > 0 && percent > 99 && percent < 100) return ">99";
    return `${Math.round(percent)}`;
  };

  const btocPercent = stats?.btoc_percent ?? 0;
  const btobPercent = stats?.btob_percent ?? 0;

  const dataBtoc = [
    { name: "Others", value: btobPercent },
    { name: "BtoC", value: btocPercent },
  ];
  const dataBtob = [
    { name: "Others", value: btocPercent },
    { name: "BtoB", value: btobPercent },
  ];

  return (
    <>
      <CustomLoader isActive={isPending || isRevenuePending} />
      <Sidebar
        styles={{
          sidebarRightRoot: classes.vehiculeRoot,
          sidebarRoot: classes.contentMain,
        }}
      >
        <div className={classes.container}>
          <div className={classes.containerHeader}>
            <div>
              <CustomTypography
                variant="h2"
                text={t("dashboard.dashboard", "Dashboard")}
                lineHeight={"47px"}
                isUppercase
              />
            </div>
          </div>
          <div className={classes.containerBody}>
            <div>
              <CustomTypography
                text={t(
                  "dashboard.overviewOfActivities",
                  "Overview of activities"
                )}
                variant={"h4"}
                isUppercase
              />
            </div>
            <div className={classes.divScroll}>
              <div className={classes.containerCarsInfos}>
                <CustomInfos
                  lightColor={colors.lightpurple}
                  darkColor={colors.darkpurple}
                  label={t("dashboard.btocUsers", "BtoC Users")}
                  numberOfUsers={stats?.btoc_users ?? 0}
                  iconName="userCircle"
                  isShowGraph
                  colorGraph="#9747FF"
                  data={dataBtoc}
                  purcent={`${formatPercent(btocPercent, stats?.btoc_users ?? 0)}%`}
                  subtitle={t("dashboard.individualUsers", "Individual users")}
                />
                <CustomInfos
                  lightColor={colors.lightBlue}
                  darkColor={colors.darkBlue}
                  label={t("dashboard.btobUsers", "BtoB Users")}
                  iconName="car"
                  numberOfUsers={stats?.btob_users ?? 0}
                  isShowGraph
                  colorGraph="#1784D2"
                  data={dataBtob}
                  purcent={`${formatPercent(btobPercent, stats?.btob_users ?? 0)}%`}
                  subtitle={t("dashboard.companyUsers", "Company users")}
                />
                <CustomInfos
                  lightColor={colors.orangeLight}
                  darkColor={colors.orangeDark}
                  label={t("dashboard.vehicles", "Vehicles")}
                  numberOfUsers={stats?.total_vehicles ?? 0}
                  iconName="car"
                  subtitle={t(
                    "dashboard.totalTrackedVehicles",
                    "Total tracked vehicles"
                  )}
                />
              </div>
              <FleetInfos
                fleetClients={stats?.fleet_clients ?? 0}
                partners={stats?.partners ?? 0}
                fleetEvolution={stats?.fleet_evolution ?? []}
                partnerEvolution={stats?.partner_evolution ?? []}
              />
              <RevenueInfos revenue={revenue} />
            </div>
          </div>
        </div>
      </Sidebar>
    </>
  );
};
